import type { ShopPublicPaymentMethod } from '../services/shop';

export type ShopCheckoutPaymentMethodOptions = {
  cartHasGiftCardPurchase: boolean;
  walletBackedPaymentMethodsAllowed: boolean;
  paymentsReady?: boolean;
  devPaymentBypassAvailable?: boolean;
};

function isWalletMethodBlocked(
  method: ShopPublicPaymentMethod,
  walletBackedPaymentMethodsAllowed: boolean,
): boolean {
  return (
    !walletBackedPaymentMethodsAllowed &&
    (method.requiresWallet || method.channel === 'wallet')
  );
}

function isCardMethodBlocked(
  method: ShopPublicPaymentMethod,
  paymentsReady: boolean,
  devPaymentBypassAvailable: boolean,
): boolean {
  return (
    method.channel === 'card' &&
    method.requiresStripeConnect &&
    !paymentsReady &&
    !devPaymentBypassAvailable
  );
}

/** Methods shown in the checkout picker (may include disabled card when Stripe is not ready). */
export function filterShopDisplayPaymentMethods(
  methods: ShopPublicPaymentMethod[],
  options: Pick<
    ShopCheckoutPaymentMethodOptions,
    'cartHasGiftCardPurchase' | 'walletBackedPaymentMethodsAllowed'
  >,
): ShopPublicPaymentMethod[] {
  return methods.filter((method) => {
    if (options.cartHasGiftCardPurchase && method.channel === 'gift_card') {
      return false;
    }
    if (isWalletMethodBlocked(method, options.walletBackedPaymentMethodsAllowed)) {
      return false;
    }
    return true;
  });
}

/** Methods the customer can actually use to complete checkout. */
export function filterShopCheckoutPaymentMethods(
  methods: ShopPublicPaymentMethod[],
  options: ShopCheckoutPaymentMethodOptions,
): ShopPublicPaymentMethod[] {
  const paymentsReady = options.paymentsReady ?? false;
  const devPaymentBypassAvailable = options.devPaymentBypassAvailable ?? false;

  return filterShopDisplayPaymentMethods(methods, options).filter((method) => {
    if (isCardMethodBlocked(method, paymentsReady, devPaymentBypassAvailable)) {
      return false;
    }
    return true;
  });
}

export function isShopPaymentMethodSelectable(
  method: ShopPublicPaymentMethod,
  options: ShopCheckoutPaymentMethodOptions,
): boolean {
  return filterShopCheckoutPaymentMethods([method], options).length > 0;
}

export function getShopPaymentMethodsEmptyMessage(options: {
  catalogMethodCount: number;
  cartHasGiftCardPurchase: boolean;
  paymentsReady: boolean;
  devPaymentBypassAvailable: boolean;
}): { title: string; body: string } {
  if (options.catalogMethodCount === 0) {
    return {
      title: 'No payment methods available',
      body: 'Enable Credit, Wallet, and/or Gift card under Settings → Payment Methods in admin.',
    };
  }

  if (
    options.cartHasGiftCardPurchase &&
    !options.paymentsReady &&
    !options.devPaymentBypassAvailable
  ) {
    return {
      title: 'Card payments required',
      body: 'Buying a gift card online requires card checkout. Finish Stripe Connect under Integrations → Stripe in admin, then try again.',
    };
  }

  return {
    title: 'No payment methods available',
    body: 'Sign in if you have a club wallet, or ask the club to finish online card payment setup (Stripe Connect).',
  };
}

/** Methods that can cover a gift-card remainder (never gift card itself). */
export function filterShopSecondaryPaymentMethods(
  methods: ShopPublicPaymentMethod[],
  walletBackedPaymentMethodsAllowed: boolean,
  options?: Pick<ShopCheckoutPaymentMethodOptions, 'paymentsReady' | 'devPaymentBypassAvailable'>,
): ShopPublicPaymentMethod[] {
  return filterShopCheckoutPaymentMethods(methods, {
    cartHasGiftCardPurchase: false,
    walletBackedPaymentMethodsAllowed,
    paymentsReady: options?.paymentsReady,
    devPaymentBypassAvailable: options?.devPaymentBypassAvailable,
  }).filter((method) => method.channel !== 'gift_card');
}

/** Prefer card, then wallet, then first remaining selectable option. */
export function defaultShopPaymentMethodCode(
  methods: ShopPublicPaymentMethod[],
): string | undefined {
  if (!methods.length) return undefined;
  const card = methods.find((m) => m.channel === 'card');
  if (card) return card.code;
  const wallet = methods.find((m) => m.channel === 'wallet');
  if (wallet) return wallet.code;
  return methods[0]?.code;
}
